/**
 * Classification logic for the router-level error screen (RouteErrorPage owns the browser
 * bits — sessionStorage and reload — this module stays pure for testing).
 */

/**
 * Message shapes browsers emit when a lazy route chunk fails to load: Chrome/Edge ("Failed to
 * fetch dynamically imported module"), Firefox ("error loading dynamically imported module"),
 * Safari ("Importing a module script failed"). Two real causes share these messages — a deploy
 * replaced the hashed filename while this tab held the old index.html (fixed by one reload), or
 * the server/network is genuinely unreachable (a reload won't help).
 */
const CHUNK_ERROR_PATTERN =
  /dynamically imported module|Importing a module script failed|Failed to fetch module script/i;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR_PATTERN.test(error.message);
}

export const CHUNK_RELOAD_STAMP_KEY = 'pb-chunk-reload-utc';

/**
 * One automatic reload per window. A stale-after-deploy chunk is fixed by the first reload, so
 * a second failure inside the window means the portal is unreachable — show the error screen
 * instead of reload-looping. The window (not a once-per-session flag) means the NEXT deploy
 * weeks later still gets its automatic fix-up in a long-lived PWA session.
 */
export const CHUNK_RELOAD_WINDOW_MS = 30_000;

export function shouldAutoReload(lastReloadStamp: string | null, nowMs: number): boolean {
  if (!lastReloadStamp) return true;
  const stamp = Number(lastReloadStamp);
  if (!Number.isFinite(stamp)) return true;
  return nowMs - stamp > CHUNK_RELOAD_WINDOW_MS;
}

// Storage access is guarded (locked-down webviews and blocked-cookies modes throw on the
// sessionStorage PROPERTY ACCESS itself) and fails CLOSED: if the loop-brake stamp cannot be
// read or written, never auto-reload — show the error screen instead of risking a reload loop.
// RouteErrorPage is the root errorElement, so an exception here would blank the whole app at
// exactly the moment it is already broken (same pattern as applyClubTheme's cache guards).

export function shouldAutoReloadNow(nowMs: number): boolean {
  try {
    return shouldAutoReload(sessionStorage.getItem(CHUNK_RELOAD_STAMP_KEY), nowMs);
  } catch {
    return false;
  }
}

/** Stamps the reload attempt; returns false (do NOT reload) when the stamp can't be written. */
export function markReloadAttempt(nowMs: number): boolean {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_STAMP_KEY, String(nowMs));
    return true;
  } catch {
    return false;
  }
}

export function clearReloadStamp(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_STAMP_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
